import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { destinationFor, droppableId, type DroppableData } from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useTransferDropHighlight } from "@/dnd/useTransferDropHighlight";
import { RailDisclosureIcon } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import {
  useRailRowContextMenu,
  type RailRowMenuController,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import type { CanvasFolderActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasFolderTree";
import type { Folder, InboxItem } from "@cohesive/api-client";
import { useDroppable } from "@dnd-kit/core";
import {
  Folder as FolderIcon,
  FolderOpen,
  FolderPlus,
  MessageSquarePlus,
  Pencil,
  Trash2,
} from "lucide-react";
import { useMemo } from "react";

/**
 * Drop target for a chat folder header. Dropping a chat-session re-files it
 * (a move, §5.4: the rail is an organizing surface). Header-only — the expanded
 * children aren't a target, so dragging a chat *out* of a folder isn't
 * recaptured by it. Highlight follows the shared validity rule.
 */
export function ChatFolderDropTarget({
  workspaceId,
  folderId,
  children,
  className,
  dropId: dropIdOverride,
}: {
  workspaceId: string;
  folderId: string | null;
  children: React.ReactNode;
  className?: string;
  /** Distinct droppable id for off-rail mounts (e.g. detail-page folder
   *  cards) — dnd-kit ids must be unique, and the rail may show the same
   *  folder at the same time. Destination semantics are unchanged. */
  dropId?: string;
}) {
  const dropId =
    dropIdOverride ?? droppableId.railChatFolder(workspaceId, folderId);

  const droppableData = useMemo(
    (): DroppableData => ({
      surface: "rail-chat-folder",
      destination: destinationFor("rail-chat-folder", {
        workspaceId,
        folderId,
      }),
    }),
    [workspaceId, folderId],
  );
  const droppable = useDroppable({ id: dropId, data: droppableData });
  // Organizing surface: active for every drag. A chat shows a live "Move" ring;
  // a file/canvas shows the grayed "can't move here" ring — never inert.
  const { isOver, disallowed } = useTransferDropHighlight(
    dropId,
    droppableData.destination,
  );

  return (
    <div
      ref={droppable.setNodeRef}
      className={cnTransferDropTargetSurface({ isOver, disallowed, className })}
    >
      {children}
    </div>
  );
}

/**
 * Three-dot actions menu for chat folder rows. Mirrors the canvas folder menu
 * (Open / New chat / New folder / Rename / Archive) so the chats rail matches
 * the canvas + files rails and the detail-page folder menus.
 */
function ChatFolderMenu({
  controller,
  label,
  onOpen,
  onNewChat,
  onNewSubfolder,
  onRename,
  onDelete,
}: {
  controller: RailRowMenuController;
  label: string;
  onOpen: () => void;
  onNewChat: () => void;
  onNewSubfolder: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <RailRowMenu {...railRowMenuProps(controller)} label={label}>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        <FolderOpen />
        Open
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onNewChat, 0);
        }}
      >
        <MessageSquarePlus />
        New chat
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onNewSubfolder, 0);
        }}
      >
        <FolderPlus />
        New folder
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onRename, 0);
        }}
      >
        <Pencil />
        Rename
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        danger
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onDelete, 0);
        }}
      >
        <Trash2 />
        Archive
      </DropdownMenuItem>
    </RailRowMenu>
  );
}

/**
 * Expandable chat folder row — mirrors the canvas folder row. The hover-reveal
 * group scopes to the header row only (`group/row`), so hovering a child row
 * never reveals this folder's kebab.
 */
function ChatFolderRow({
  workspaceId,
  folder,
  actions,
  children,
}: {
  workspaceId: string;
  folder: Folder;
  actions: CanvasFolderActions;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useRailExpansion(`folder:${folder.id}`);
  const menu = useRailRowContextMenu();

  return (
    <div className="flex flex-col">
      {/* Only the header row is a drop target — not the expanded children — so
          dragging a chat *out* of this folder isn't recaptured by it. */}
      <ChatFolderDropTarget workspaceId={workspaceId} folderId={folder.id}>
        <div
          onContextMenu={menu.onContextMenu}
          className="group/row relative flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 pr-8 hover:bg-n-05"
        >
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            data-state={open ? "open" : "closed"}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
          >
            <RailDisclosureIcon
              open={open}
              icon={open ? FolderOpen : FolderIcon}
            />
            <span className="flex-1 truncate text-base text-n-80">
              {folder.name}
            </span>
          </button>
          <ChatFolderMenu
            controller={menu}
            label={folder.name}
            onOpen={() => actions.onOpen(folder)}
            onNewChat={() => actions.onNewResource(folder.id)}
            onNewSubfolder={() => actions.onNewSubfolder(folder.id)}
            onRename={() => actions.onRename(folder)}
            onDelete={() => actions.onDelete(folder)}
          />
        </div>
      </ChatFolderDropTarget>
      {open && <div className="flex flex-col pl-2">{children}</div>}
    </div>
  );
}

/**
 * Recursive chat folder tree for one workspace, mirroring the canvas folder
 * tree: renders the folders whose parent is `parentFolderId` (each containing
 * its own subtree) then the chats filed directly under `parentFolderId`.
 * `renderChatRow` is supplied by the Chats section so per-chat handlers live in
 * one place. Membership reads from the chat's nested `flow_session.folder_id`.
 */
export function WorkspacesRailChatFolderTree({
  workspaceId,
  parentFolderId,
  folders,
  chats,
  renderChatRow,
  folderActions,
}: {
  workspaceId: string;
  parentFolderId: string | null;
  folders: Folder[];
  chats: InboxItem[];
  renderChatRow: (chat: InboxItem) => React.ReactNode;
  folderActions: CanvasFolderActions;
}) {
  const childFolders = useMemo(
    () =>
      folders
        .filter((f) => (f.parent_folder_id ?? null) === parentFolderId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [folders, parentFolderId],
  );

  const folderChats = useMemo(
    () =>
      chats.filter(
        (c) => (c.flow_session?.folder_id ?? null) === parentFolderId,
      ),
    [chats, parentFolderId],
  );

  return (
    <>
      {childFolders.map((folder) => (
        <ChatFolderRow
          key={folder.id}
          workspaceId={workspaceId}
          folder={folder}
          actions={folderActions}
        >
          <WorkspacesRailChatFolderTree
            workspaceId={workspaceId}
            parentFolderId={folder.id}
            folders={folders}
            chats={chats}
            renderChatRow={renderChatRow}
            folderActions={folderActions}
          />
        </ChatFolderRow>
      ))}
      {folderChats.map((chat) => renderChatRow(chat))}
    </>
  );
}
